// Wi-Fi 重配模式決策原語（device/09 §3 corrected — decideReconfigMode 的 SSOT）。
//
// 決策矩陣（三端同語意）：
//   online                          -> cloud      （模式 A：雲端 WifiReconfigure command）
//   offline + hasBLE                -> ble        （模式 B：實體按鍵觸發的本地 BLE 重配）
//   offline + !hasBLE + hasSoftAP   -> softAp     （模式 C：SoftAP）
//   offline + 皆無                  -> lastResort （手動：把裝置移回舊網 / 回復原廠）
// 其中 hasBLE = transports 含 'ble'、hasSoftAP = transports 含 'softap'。
//
// 這是純函式：無 I/O、不依賴 device 物件，可脫離 DOM/emulator 單元測試。

import type { DeviceSummary } from '../deviceModel';

export type ReconfigMode = 'cloud' | 'ble' | 'softAp' | 'lastResort';

/**
 * 決策原語（三端 identical semantics）。純函式，僅依 online 布林與 transports 清單。
 * transports 值比對大小寫無關；未知值一律忽略（不影響 hasBLE/hasSoftAP）。
 * 空/缺 transports => hasBLE=false, hasSoftAP=false => online?cloud:lastResort，
 * 這是與今日行為一致的安全預設（尚無機型回填 transports）。
 */
export function decideReconfigMode(online: boolean, transports: string[]): ReconfigMode {
  if (online) return 'cloud';
  const set = new Set(transports.map((t) => t.toLowerCase()));
  if (set.has('ble')) return 'ble';
  if (set.has('softap')) return 'softAp';
  return 'lastResort';
}

/**
 * 便捷 wrapper：以平台 Device 呼叫原語。
 * online 採嚴格判定：唯有 online.state === 'online' 才算在線；'stale'/'offline'/缺值
 * 一律 NOT online（保守：off/stale 時不走雲端 command，改走本地/手動路徑）。
 * transports 缺值退到 []（=> 安全預設 lastResort，見上）。
 */
export function decideForDevice(d: DeviceSummary): ReconfigMode {
  const online = d.online?.state === 'online';
  return decideReconfigMode(online, d.transports ?? []);
}
